import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ensureFreeGiftTimer,
  removeExpiredFreeGifts,
  getCartSectionIds,
  isFreeGiftParentProduct,
  isFreeGiftLineItem,
} from '@theme/free-gift-bundle';

/**
 * Countdown timer for the free-gift cart promotion (5 minutes from first add).
 *
 * @typedef {object} FreeGiftTimerRefs
 * @property {HTMLElement} time - The countdown display element.
 *
 * @extends {Component<FreeGiftTimerRefs>}
 */
class FreeGiftTimer extends Component {
  requiredRefs = ['time'];

  /** @type {number | undefined} */
  #intervalId;

  /** @type {boolean} */
  #expiring = false;

  /** @type {boolean} */
  #bootstrapping = false;

  connectedCallback() {
    super.connectedCallback();
    void this.#bootstrap();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const nextExpires = event?.detail?.resource?.attributes?.free_gift_expires_at;
    if (nextExpires) {
      this.dataset.expiresAt = nextExpires;
    }
    this.#tick();
  };

  async #bootstrap() {
    if (this.#bootstrapping) return;
    this.#bootstrapping = true;

    try {
      let expiresAt = (this.dataset.expiresAt || '').trim();
      let expiresMs = expiresAt ? Date.parse(expiresAt) : NaN;
      const valid = Number.isFinite(expiresMs) && expiresMs > Date.now();

      if (!valid) {
        const sectionIds = this.dataset.sectionId ? [this.dataset.sectionId] : getCartSectionIds();
        const result = await ensureFreeGiftTimer(sectionIds);
        expiresAt =
          result?.__expiresAt ||
          result?.attributes?.free_gift_expires_at ||
          '';

        if (expiresAt) {
          this.dataset.expiresAt = expiresAt;
        }
      }

      this.#start();
    } catch (error) {
      console.warn('Free gift timer bootstrap failed', error);
      this.refs.time.textContent = '05:00';
    } finally {
      this.#bootstrapping = false;
    }
  }

  #start() {
    this.#tick();
    if (this.#intervalId) clearInterval(this.#intervalId);
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  #tick() {
    const expiresAt = (this.dataset.expiresAt || '').trim();
    if (!expiresAt) {
      this.refs.time.textContent = '--:--';
      return;
    }

    const remaining = Date.parse(expiresAt) - Date.now();

    if (!Number.isFinite(remaining)) {
      this.refs.time.textContent = '--:--';
      return;
    }

    if (remaining <= 0) {
      this.refs.time.textContent = '00:00';
      this.classList.add('free-gift-timer--expired');
      this.#handleExpired();
      return;
    }

    this.hidden = false;
    this.classList.toggle('free-gift-timer--urgent', remaining <= 60_000);
    this.refs.time.textContent = formatCountdown(remaining);
  }

  async #handleExpired() {
    if (this.#expiring) return;
    this.#expiring = true;
    this.#stop();

    try {
      const result = await removeExpiredFreeGifts();
      if (!result) {
        this.classList.add('free-gift-timer--expired');
        return;
      }

      const sectionId = this.dataset.sectionId;
      if (sectionId && result.sections?.[sectionId]) {
        morphSection(sectionId, result.sections[sectionId], {
          mode: this.dataset.isDrawer === 'true' ? 'hydration' : 'full',
        });
      } else if (sectionId) {
        sectionRenderer.renderSection(sectionId, { cache: false });
      }

      document.dispatchEvent(
        new CartUpdateEvent(result, sectionId || 'free-gift-timer', {
          source: 'free-gift-timer',
          itemCount: result.item_count,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#expiring = false;
    }
  }
}

/**
 * @param {number} ms
 * @returns {string}
 */
function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

if (!customElements.get('free-gift-timer')) {
  customElements.define('free-gift-timer', FreeGiftTimer);
}

/**
 * @param {{ items?: Array<{ product_id?: string | number, properties?: Record<string, string> | null }> } | null | undefined} cart
 * @returns {boolean}
 */
function cartHasPromo(cart) {
  return (cart?.items || []).some(
    (item) => isFreeGiftParentProduct(item.product_id) || isFreeGiftLineItem(item)
  );
}

/**
 * Site-wide countdown banner above the header. Morphs with cart updates
 * so it appears as soon as Máscara Led is added and hides when the offer ends.
 */
class FreeGiftSiteBanner extends HTMLElement {
  connectedCallback() {
    this.hidden = !this.querySelector('free-gift-timer');
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {CartUpdateEvent} event */
  #onCartUpdate = async (event) => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || event.detail?.sourceId === sectionId) return;

    const cart = event.detail?.resource;
    const hasItems = Array.isArray(cart?.items);
    const showing = Boolean(this.querySelector('free-gift-timer'));

    if (hasItems && cartHasPromo(cart) === showing) return;

    const sectionHtml = event.detail?.data?.sections?.[sectionId];

    try {
      if (sectionHtml) {
        await morphSection(sectionId, sectionHtml);
        return;
      }

      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      try {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      } catch (fallbackError) {
        console.warn('Free gift site banner update failed', fallbackError || error);
      }
    }
  };
}

if (!customElements.get('free-gift-site-banner')) {
  customElements.define('free-gift-site-banner', FreeGiftSiteBanner);
}
